import { strToU8, zipSync } from 'fflate';
import OlMap from 'ol/Map';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import MVT from 'ol/format/MVT';
import { fromExtent } from 'ol/geom/Polygon';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import ImageStatic from 'ol/source/ImageStatic';
import { OGCMapTile, OGCVectorTile } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import VectorTile from 'ol/VectorTile';
import RenderFeature from 'ol/render/Feature';
import TileState from 'ol/TileState';
import type Tile from 'ol/Tile';
import { Fill, Stroke, Style } from 'ol/style';
import type { FeatureLike } from 'ol/Feature';

import { CANVAS_COLOR_VARS, getCanvasColor } from '../canvas-theme.utils';

import {
  markerIconStyle,
  vectorColorStyle,
} from './layers/canvas-layer-style.utils';
import type { CanvasExportLayerDescriptor } from './canvas-maps.models';
import type { Extent } from 'ol/extent';
import type { LoadFunction } from 'ol/Tile';
import type { Projection } from 'ol/proj';

function sanitizeExportFileName(name: string): string {
  return name.trim().replace(/[/\\?%*:|"<>]/g, '-');
}

function uniqueGeoJsonFileName(baseName: string, used: Set<string>): string {
  const safe = sanitizeExportFileName(baseName) || 'layer';
  if (!used.has(safe)) {
    used.add(safe);
    return safe;
  }
  let i = 2;
  while (used.has(`${safe}_${i}`)) i += 1;
  const unique = `${safe}_${i}`;
  used.add(unique);
  return unique;
}

/**
 * Composites OpenLayers layer canvases into one JPEG (ui-canvas `exportAsImage` parity).
 * Keeps the live map target intact — `setTarget(canvas)` is invalid because OL mounts a
 * viewport div inside the target element.
 */
export function exportMapAsJpegDataUrl(map: OlMap): Promise<string> {
  return new Promise((resolve, reject) => {
    const allCanvas = map
      .getViewport()
      .querySelectorAll('.ol-layer canvas, canvas.ol-layer') as NodeListOf<HTMLCanvasElement>;

    if (!allCanvas.length || allCanvas[0].width <= 0 || allCanvas[0].height <= 0) {
      reject(new Error('Map has no render layers; cannot export'));
      return;
    }

    const mapCanvas = document.createElement('canvas');
    mapCanvas.width = allCanvas[0].width;
    mapCanvas.height = allCanvas[0].height;
    const mapContext = mapCanvas.getContext('2d');
    if (!mapContext) {
      reject(new Error('Could not create export canvas'));
      return;
    }

    map.once('rendercomplete', () => {
      try {
        for (const canvas of Array.from(allCanvas)) {
          if (canvas.width <= 0) continue;

          mapContext.globalAlpha = 1;
          let matrix: number[] = [
            parseFloat(canvas.style.width) / canvas.width,
            0,
            0,
            parseFloat(canvas.style.height) / canvas.height,
            0,
            0,
          ];
          const transform = canvas.style.transform;
          if (transform) {
            const match = transform.match(/^matrix\(([^(]*)\)$/);
            if (match) {
              matrix = match[1].split(',').map(Number);
            }
          }
          mapContext.setTransform(
            matrix[0],
            matrix[1],
            matrix[2],
            matrix[3],
            matrix[4],
            matrix[5],
          );
          mapContext.drawImage(canvas, 0, 0);
        }
        resolve(mapCanvas.toDataURL('image/jpeg', 0.92));
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });
    map.renderSync();
  });
}

/** Serialize features from a vector source as a GeoJSON FeatureCollection. */
export function exportFeaturesAsGeoJson(source: VectorSource): string {
  const features = source.getFeatures();
  const format = new GeoJSON();
  const json = format.writeFeaturesObject(features, {
    featureProjection: 'EPSG:4326',
    dataProjection: 'EPSG:4326',
  });
  return JSON.stringify(json, null, 2);
}

/** Serialize only features belonging to a layers-panel entry (`cvEntryId`). */
export function exportLayerFeaturesAsGeoJson(
  source: VectorSource,
  entryId: string,
): string {
  const features = source
    .getFeatures()
    .filter((f) => f.get('cvEntryId') === entryId);
  const format = new GeoJSON();
  const json = format.writeFeaturesObject(features, {
    featureProjection: 'EPSG:4326',
    dataProjection: 'EPSG:4326',
  });
  return JSON.stringify(json, null, 2);
}

/** Zip visible canvas layers as separate GeoJSON files (ui-canvas `exportAsGeoJson` parity). */
export function exportCanvasLayersAsGeoJsonZip(
  layers: readonly CanvasExportLayerDescriptor[],
): Blob | null {
  const files: Record<string, Uint8Array> = {};
  const usedNames = new Set<string>();

  for (const layer of layers) {
    const features = layer.source
      .getFeatures()
      .filter((f) => f.get('cvEntryId') === layer.entryId);
    if (features.length === 0) continue;

    const json = exportLayerFeaturesAsGeoJson(layer.source, layer.entryId);
    const fileName = uniqueGeoJsonFileName(layer.name, usedNames);
    files[`${fileName}.geojson`] = strToU8(json);
  }

  if (Object.keys(files).length === 0) return null;
  return new Blob([zipSync(files)], { type: 'application/zip' });
}

export type StacBbox = [number, number, number, number];

export interface PlottedOlLayerBundle {
  layerId: string;
  olLayer:
    | ImageLayer<ImageStatic>
    | VectorLayer<VectorSource>
    | VectorTileLayer
    | TileLayer;
  source?: VectorSource;
}

/** STAC overview image as static extent layer (ui-canvas `createNewStacImageLayer`). */
export function createStacImageLayer(
  imageUrl: string,
  bbox: StacBbox,
  layerId: string,
): PlottedOlLayerBundle {
  const source = new ImageStatic({
    url: imageUrl,
    imageExtent: bbox,
    projection: 'EPSG:4326',
    crossOrigin: 'anonymous',
  });
  const olLayer = new ImageLayer({ source, zIndex: 92 });
  olLayer.set('cvEntryId', layerId);
  return { layerId, olLayer };
}

/** STAC footprint when no overview image (ui-canvas `drawBBoxFromApi`). */
export function createBboxBoundaryLayer(
  bbox: StacBbox,
  layerId: string,
  layerColor = getCanvasColor(CANVAS_COLOR_VARS.layerDefault),
): PlottedOlLayerBundle {
  const source = new VectorSource();
  const feature = new Feature(fromExtent(bbox));
  feature.set('cvEntryId', layerId);
  feature.set('cvLayerColor', layerColor);
  source.addFeature(feature);
  const olLayer = new VectorLayer({
    source,
    zIndex: 92,
    style: (feat) => {
      const color =
        (feat.get('cvLayerColor') as string | undefined) ?? layerColor;
      return vectorColorStyle(color, feat as FeatureLike);
    },
  });
  olLayer.set('cvEntryId', layerId);
  return { layerId, olLayer, source };
}

/** Vector layer style callback reading per-feature `cvLayerColor` / `cvMarkerIconId`. */
export function createDatasetVectorStyleFn(
  iconsBaseUrl: string,
  fallbackColor: string,
  fallbackMarkerId: number,
): (feat: FeatureLike) => Style | undefined {
  return (feat) => {
    const color = (feat.get('cvLayerColor') as string | undefined) ?? fallbackColor;
    const markerId =
      (feat.get('cvMarkerIconId') as number | undefined) ?? fallbackMarkerId;
    const geom = feat.getGeometry();
    if (!geom) return undefined;
    const type = geom.getType();
    if (type === 'Point' || type === 'MultiPoint') {
      return markerIconStyle(iconsBaseUrl, markerId);
    }
    return vectorColorStyle(color, feat);
  };
}

function tileLoadWithBearer(token: string, accept: string): LoadFunction {
  return (tile: Tile, url: string) => {
    const vectorTile = tile as VectorTile<RenderFeature>;
    vectorTile.setLoader(
      (extent: Extent, _res: number, projection: Projection) => {
      fetch(url, {
        headers: {
          Accept: accept,
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            vectorTile.setState(TileState.ERROR);
            return;
          }
          return response.arrayBuffer().then((data) => {
            const format = vectorTile.getFormat();
            const features = format.readFeatures(data, {
              extent,
              featureProjection: projection,
            });
            vectorTile.setFeatures(features);
          });
        })
        .catch(() => vectorTile.setState(TileState.ERROR));
      },
    );
  };
}

/** OGC vector tiles for a collection (ui-canvas `createNewUgixTileLayer`). */
export function createVectorTileLayerFromUrl(
  tilesUrl: string,
  token: string,
  layerId: string,
): PlottedOlLayerBundle {
  const source = new OGCVectorTile({
    url: tilesUrl,
    format: new MVT(),
    mediaType: 'application/vnd.mapbox-vector-tile',
  });
  source.setTileLoadFunction(
    tileLoadWithBearer(token, 'application/vnd.mapbox-vector-tile'),
  );
  const olLayer = new VectorTileLayer({
    source,
    zIndex: 90,
  });
  olLayer.set('cvEntryId', layerId);
  return { layerId, olLayer };
}

/** OGC raster map tiles (ui-canvas `createNewRasterTileLayer`). */
export function createRasterTileLayerFromUrl(
  tilesUrl: string,
  token: string,
  layerId: string,
): PlottedOlLayerBundle {
  const source = new OGCMapTile({ url: tilesUrl });
  source.setTileLoadFunction(async (tile, url) => {
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        tile.setState(TileState.ERROR);
        return;
      }
      const blob = await response.blob();
      const imageTile = tile as Tile & { getImage?: () => HTMLImageElement };
      const img = imageTile.getImage?.();
      if (img) {
        img.src = URL.createObjectURL(blob);
      }
    } catch {
      tile.setState(TileState.ERROR);
    }
  });
  const olLayer = new TileLayer({ source, zIndex: 90 });
  olLayer.set('cvEntryId', layerId);
  return { layerId, olLayer };
}

/** Fit map view to an extent in EPSG:4326. */
export function fitMapToExtent(map: OlMap, extent: Extent): void {
  map.getView().fit(extent, {
    padding: [48, 48, 48, 48],
    maxZoom: 14,
    duration: 400,
  });
}
